<template>
  <div v-if="visible" class="mcl-schedule" @click.self="closeAll">
    <div v-if="step === 'menu'" class="mcl-schedule__menu" :style="menuStyle" @click.stop>
      <p class="mcl-schedule__menu-title">Schedule send</p>
      <p class="mcl-schedule__menu-tz">{{ timezoneLabel }}</p>
      <ul class="mcl-schedule__options">
        <li v-for="option in presetOptions" :key="option.key">
          <button type="button" class="mcl-schedule__option" @click.stop="selectPreset(option)">
            <span class="mcl-schedule__option-label">{{ option.label }}</span>
            <span class="mcl-schedule__option-time">{{ option.displayTime }}</span>
          </button>
        </li>
        <li>
          <button type="button" class="mcl-schedule__option mcl-schedule__option--custom" @click.stop="openPicker">
            <span class="mcl-schedule__option-label">
              <font-awesome-icon :icon="['fas', 'calendar']" />
              Pick date &amp; time
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div v-if="step === 'picker'" class="mcl-schedule__picker-backdrop" @click.stop>
      <div class="mcl-schedule__picker" @click.stop>
        <div class="mcl-schedule__picker-head">
          <h3 class="mcl-schedule__picker-title">Pick date &amp; time</h3>
          <button type="button" class="mcl-schedule__picker-close" title="Close" @click.stop="closeAll">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
        <div class="mcl-schedule__picker-body">
          <div class="mcl-schedule__picker-calendar">
            <div class="mcl-schedule__picker-nav">
              <button type="button" class="mcl-schedule__picker-nav-btn" @click.stop="prevMonth">
                <font-awesome-icon :icon="['fas', 'chevron-left']" />
              </button>
              <span class="mcl-schedule__picker-month">{{ calendarMonthLabel }}</span>
              <button type="button" class="mcl-schedule__picker-nav-btn" @click.stop="nextMonth">
                <font-awesome-icon :icon="['fas', 'chevron-right']" />
              </button>
            </div>
            <div class="mcl-schedule__picker-weekdays">
              <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
            </div>
            <div class="mcl-schedule__picker-days">
              <button
                v-for="cell in calendarCells"
                :key="cell.key"
                type="button"
                class="mcl-schedule__picker-day"
                :class="{
                  'mcl-schedule__picker-day--muted': !cell.inMonth,
                  'mcl-schedule__picker-day--selected': cell.selected,
                  'mcl-schedule__picker-day--disabled': cell.disabled,
                }"
                :disabled="cell.disabled"
                @click.stop="selectCalendarDay(cell)"
              >
                {{ cell.day }}
              </button>
            </div>
          </div>
          <div class="mcl-schedule__picker-fields">
            <label class="mcl-schedule__field">
              <span>Date</span>
              <input v-model="customDate" type="date" class="mcl-schedule__input" :min="minDate" />
            </label>
            <label class="mcl-schedule__field">
              <span>Time</span>
              <input v-model="customTime" type="time" class="mcl-schedule__input" />
            </label>
          </div>
        </div>
        <div class="mcl-schedule__picker-footer">
          <button type="button" class="mcl-schedule__btn mcl-schedule__btn--ghost" @click.stop="backToMenu">Cancel</button>
          <button type="button" class="mcl-schedule__btn mcl-schedule__btn--primary" @click.stop="confirmCustom">
            Schedule send
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "schedule-send-modals",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    anchorRect: {
      type: Object,
      default: null,
    },
  },
  emits: ["close", "schedule"],
  data: function () {
    return {
      step: "menu",
      calendarMonth: new Date(),
      customDate: "",
      customTime: "",
    };
  },
  computed: {
    timezoneLabel: function () {
      try {
        var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz || "Local time";
      } catch (e) {
        return "Local time";
      }
    },
    menuStyle: function () {
      if (!this.anchorRect) {
        return {
          bottom: "4.5rem",
          right: "1.5rem",
        };
      }
      return {
        bottom: window.innerHeight - this.anchorRect.top + 8 + "px",
        right: window.innerWidth - this.anchorRect.right + "px",
      };
    },
    presetOptions: function () {
      var now = new Date();
      var options = [];

      var tomorrowMorning = new Date(now);
      tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
      tomorrowMorning.setHours(8, 0, 0, 0);
      options.push({
        key: "tomorrow_morning",
        label: "Tomorrow morning",
        date: tomorrowMorning,
        displayTime: this.formatPresetDate(tomorrowMorning),
      });

      var thisAfternoon = new Date(now);
      thisAfternoon.setHours(13, 0, 0, 0);
      if (thisAfternoon <= now) {
        thisAfternoon.setDate(thisAfternoon.getDate() + 1);
      }
      options.push({
        key: "this_afternoon",
        label: "This afternoon",
        date: thisAfternoon,
        displayTime: this.formatPresetDate(thisAfternoon),
      });

      var mondayMorning = this.nextMondayAt(8, 0);
      options.push({
        key: "monday_morning",
        label: "Monday morning",
        date: mondayMorning,
        displayTime: this.formatPresetDate(mondayMorning),
      });

      return options;
    },
    minDate: function () {
      return this.formatDateInputValue(new Date());
    },
    calendarMonthLabel: function () {
      return this.calendarMonth.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      });
    },
    weekdayLabels: function () {
      return ["S", "M", "T", "W", "T", "F", "S"];
    },
    calendarCells: function () {
      var year = this.calendarMonth.getFullYear();
      var month = this.calendarMonth.getMonth();
      var firstDay = new Date(year, month, 1);
      var startOffset = firstDay.getDay();
      var startDate = new Date(year, month, 1 - startOffset);
      var selected = this.customDate;
      var todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      var cells = [];

      for (var i = 0; i < 42; i++) {
        var cellDate = new Date(startDate);
        cellDate.setDate(startDate.getDate() + i);
        var dateStr = this.formatDateInputValue(cellDate);
        var cellDayStart = new Date(cellDate);
        cellDayStart.setHours(0, 0, 0, 0);

        cells.push({
          key: dateStr + "-" + i,
          day: cellDate.getDate(),
          inMonth: cellDate.getMonth() === month,
          selected: selected === dateStr,
          disabled: cellDayStart < todayStart,
          dateStr: dateStr,
        });
      }

      return cells;
    },
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.step = "menu";
        this.initCustomFields();
      }
    },
    customDate: function () {
      if (this.customDate) {
        var parts = this.customDate.split("-");
        if (parts.length === 3) {
          this.calendarMonth = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1);
        }
      }
    },
  },
  methods: {
    initCustomFields: function () {
      var defaultDate = new Date();
      defaultDate.setHours(defaultDate.getHours() + 1);
      defaultDate.setMinutes(0, 0, 0);
      this.customDate = this.formatDateInputValue(defaultDate);
      this.customTime = this.formatTimeInputValue(defaultDate);
      this.calendarMonth = new Date(defaultDate.getFullYear(), defaultDate.getMonth(), 1);
    },
    formatPresetDate: function (date) {
      return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    formatDateInputValue: function (date) {
      var y = date.getFullYear();
      var m = String(date.getMonth() + 1).padStart(2, "0");
      var d = String(date.getDate()).padStart(2, "0");
      return y + "-" + m + "-" + d;
    },
    formatTimeInputValue: function (date) {
      var h = String(date.getHours()).padStart(2, "0");
      var min = String(date.getMinutes()).padStart(2, "0");
      return h + ":" + min;
    },
    nextMondayAt: function (hour, minute) {
      var date = new Date();
      var day = date.getDay();
      var daysUntilMonday = (8 - day) % 7;
      if (daysUntilMonday === 0) {
        daysUntilMonday = 7;
      }
      date.setDate(date.getDate() + daysUntilMonday);
      date.setHours(hour, minute, 0, 0);
      return date;
    },
    selectPreset: function (option) {
      if (!option || !option.date) {
        return;
      }
      this.emitSchedule(option.date);
    },
    openPicker: function () {
      this.step = "picker";
      this.initCustomFields();
    },
    backToMenu: function () {
      this.step = "menu";
    },
    closeAll: function () {
      this.step = "menu";
      this.$emit("close");
    },
    prevMonth: function () {
      this.calendarMonth = new Date(
        this.calendarMonth.getFullYear(),
        this.calendarMonth.getMonth() - 1,
        1
      );
    },
    nextMonth: function () {
      this.calendarMonth = new Date(
        this.calendarMonth.getFullYear(),
        this.calendarMonth.getMonth() + 1,
        1
      );
    },
    selectCalendarDay: function (cell) {
      if (!cell || cell.disabled) {
        return;
      }
      this.customDate = cell.dateStr;
    },
    confirmCustom: function () {
      if (!this.customDate || !this.customTime) {
        return;
      }
      var parts = this.customDate.split("-");
      var timeParts = this.customTime.split(":");
      if (parts.length !== 3 || timeParts.length < 2) {
        return;
      }
      var scheduled = new Date(
        parseInt(parts[0], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[2], 10),
        parseInt(timeParts[0], 10),
        parseInt(timeParts[1], 10),
        0,
        0
      );
      if (scheduled <= new Date()) {
        return;
      }
      this.emitSchedule(scheduled);
    },
    formatDateTimeForGraphql: function (date) {
      var d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) {
        return "";
      }
      var y = d.getFullYear();
      var m = String(d.getMonth() + 1);
      if (m.length < 2) {
        m = "0" + m;
      }
      var day = String(d.getDate());
      if (day.length < 2) {
        day = "0" + day;
      }
      var h = String(d.getHours());
      if (h.length < 2) {
        h = "0" + h;
      }
      var min = String(d.getMinutes());
      if (min.length < 2) {
        min = "0" + min;
      }
      var sec = String(d.getSeconds());
      if (sec.length < 2) {
        sec = "0" + sec;
      }
      return y + "-" + m + "-" + day + " " + h + ":" + min + ":" + sec;
    },
    emitSchedule: function (date) {
      this.$emit("schedule", this.formatDateTimeForGraphql(date));
      this.closeAll();
    },
  },
};
</script>

<style scoped>
.mcl-schedule {
  position: fixed;
  inset: 0;
  z-index: 260;
  pointer-events: none;
}

.mcl-schedule__menu {
  position: fixed;
  pointer-events: auto;
  width: 280px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  border: 1px solid #dadce0;
  overflow: hidden;
}

.mcl-schedule__menu-title {
  margin: 0;
  padding: 1rem 1rem 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #202124;
}

.mcl-schedule__menu-tz {
  margin: 0;
  padding: 0 1rem 0.75rem;
  font-size: 0.75rem;
  color: #5f6368;
}

.mcl-schedule__options {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mcl-schedule__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  font-size: 0.875rem;
  color: #202124;
  border-top: 1px solid #f1f3f4;
}

.mcl-schedule__option:hover {
  background: #f8f9fa;
}

.mcl-schedule__option-label {
  font-weight: 400;
}

.mcl-schedule__option-time {
  font-size: 0.8125rem;
  color: #5f6368;
}

.mcl-schedule__option--custom .mcl-schedule__option-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.mcl-schedule__picker-backdrop {
  position: fixed;
  inset: 0;
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.mcl-schedule__picker {
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.mcl-schedule__picker-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e8eaed;
}

.mcl-schedule__picker-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: #202124;
}

.mcl-schedule__picker-close {
  border: none;
  background: none;
  color: #5f6368;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1rem;
}

.mcl-schedule__picker-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 1rem 1.25rem;
}

@media (min-width: 520px) {
  .mcl-schedule__picker-body {
    grid-template-columns: 1.2fr 0.8fr;
  }
}

.mcl-schedule__picker-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.mcl-schedule__picker-month {
  font-size: 0.875rem;
  font-weight: 500;
  color: #202124;
}

.mcl-schedule__picker-nav-btn {
  border: none;
  background: none;
  color: #5f6368;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.mcl-schedule__picker-nav-btn:hover {
  background: #f1f3f4;
}

.mcl-schedule__picker-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #5f6368;
  margin-bottom: 0.25rem;
}

.mcl-schedule__picker-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.mcl-schedule__picker-day {
  aspect-ratio: 1;
  border: none;
  background: none;
  border-radius: 50%;
  font-size: 0.8125rem;
  color: #202124;
  cursor: pointer;
}

.mcl-schedule__picker-day:hover:not(:disabled) {
  background: #f1f3f4;
}

.mcl-schedule__picker-day--muted {
  color: #9aa0a6;
}

.mcl-schedule__picker-day--selected {
  background: #1a73e8;
  color: #ffffff;
}

.mcl-schedule__picker-day--disabled {
  color: #dadce0;
  cursor: not-allowed;
}

.mcl-schedule__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
}

.mcl-schedule__field span {
  font-size: 0.75rem;
  font-weight: 500;
  color: #5f6368;
}

.mcl-schedule__input {
  width: 100%;
  padding: 0.5rem 0.625rem;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 0.875rem;
}

.mcl-schedule__input:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
}

.mcl-schedule__picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid #e8eaed;
}

.mcl-schedule__btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
}

.mcl-schedule__btn--ghost {
  background: none;
  color: #1a73e8;
}

.mcl-schedule__btn--ghost:hover {
  background: #f6fafe;
}

.mcl-schedule__btn--primary {
  background: #1a73e8;
  color: #ffffff;
}

.mcl-schedule__btn--primary:hover {
  background: #1765cc;
}
</style>
